import {
    type CSSProperties,
    type FC,
    Fragment,
    type ReactNode,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    type SaleColumnAlignmentType,
    type SaleComponentColumns,
    type SaleComponentSettings,
} from "../../../Routes/Settings/CustomerDisplay/CustomerDisplayTypes";
import { currency } from "../../../Utilities/Generic/Numbers";
import { pluralize, uppercaseFirst } from "../../../Utilities/Generic/Strings";
import { getOption, subscribeToOption, unsubscribeFromOption } from "../../../Utilities/Platform/Options/Options";
import NoTranslate from "../../NoTranslate";
import { type CustomerDisplayComponentProps, type CustomerDisplayProductData } from "../CustomerDisplay";

interface Props extends CustomerDisplayComponentProps<SaleComponentSettings, CustomerDisplayProductData> {}

const TEXT_ALIGN: Record<SaleColumnAlignmentType, CSSProperties["textAlign"]> = {
    "LEFT"  : "left",
    "CENTRE": "center",
    "RIGHT" : "right",
};

const HEADER_DEFAULT_ALIGNMENTS: Record<keyof SaleComponentColumns, CSSProperties["textAlign"]> = {
    item      : "left",
    price     : "center",
    quantity  : "center",
    totalPrice: "center",
};

const COLUMN_DEFAULT_ALIGNMENTS: Record<keyof SaleComponentColumns, CSSProperties["textAlign"]> = {
    item      : "left",
    price     : "left",
    quantity  : "left",
    totalPrice: "left",
};

export const CustomerDisplaySaleTable: FC<Props> = ({ settings, data }) => {
    const scrollRef = useRef<HTMLDivElement>(null);

    const [ itemLabel, setItemLabel ] = useState(() => {
        return uppercaseFirst(getOption("product_single_text"));
    });

    const [ caseLabel, setCaseLabel ] = useState(() => {
        return uppercaseFirst(getOption("product_case_text"));
    });

    useEffect(() => {
        const updateItem = (_: string, itemLabel: string): void => {
            setItemLabel(uppercaseFirst(itemLabel));
        };

        const updateCase = (_: string, caseLabel: string): void => {
            setCaseLabel(uppercaseFirst(caseLabel));
        };

        subscribeToOption("product_single_text", updateItem);
        subscribeToOption("product_case_text", updateCase);

        return () => {
            unsubscribeFromOption("product_single_text", updateItem);
            unsubscribeFromOption("product_case_text", updateCase);
        };
    }, []);

    useEffect(() => {
        if(scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [ data ]);

    const footer = useMemo(() => {
        let cases = 0;
        let items = 0;

        for(const product of data.products) {
            if(product.isCase) {
                cases += product.quantity;
            } else {
                items += product.quantity;
            }
        }

        let footer = "";

        if(data.surcharge !== 0) {
            footer += `Surcharge: ${currency(data.surcharge)} | `;
        }

        if(cases) {
            footer += `${cases} ${pluralize(caseLabel, cases)}`;
        }

        if(items) {
            if(cases) {
                footer += " and ";
            }

            footer += `${items} ${pluralize(itemLabel, items)}`;
        }

        return footer;
    }, [ data, caseLabel, itemLabel ]);

    let fontSize = parseFloat(settings.fontSize);

    if(isNaN(fontSize)) {
        fontSize = 16;
    }

    const style: CSSProperties = {
        backgroundColor: settings.background,
        color          : settings.color,
        fontSize       : `${fontSize}px`,
    };

    const gridStyles: CSSProperties = {};

    if(settings.gridLines) {
        gridStyles.borderColor = settings.gridColor;
    } else {
        gridStyles.border = "none";
    }

    const footerStyle: CSSProperties = {
        background: settings.footerBackground,
        color     : settings.footerColor,
    };

    const canDisplayComponents = useMemo(() => {
        for(let i = 0, l = settings.order.length; i < l; i++) {
            const key = settings.order[i];
            const column = settings.columns[key];

            if(!column.enabled) {
                continue;
            }

            if(key === "item" || key === "price" || key === "totalPrice") {
                return true;
            }
        }

        return false;
    }, [ settings.columns, settings.order ]);

    let isFirstHeader = true;

    return (
        <div className="sale-table-wrapper w-full h-full" style={style}>
            <div ref={scrollRef} className="sale-table-scroll">
                <table className="sale-table">
                    <thead>
                        <tr className="sale-header">
                            {settings.order.map(key => {
                                const column = settings.columns[key];

                                if(!column.enabled) {
                                    return;
                                }

                                const colClasses = [ "cd-col", `cd-${key}` ];

                                if(settings.gridLines) {
                                    colClasses.push("cd-grid");
                                }

                                const colSpan = isFirstHeader ? 2 : 1;

                                isFirstHeader = false;

                                return (
                                    <th
                                        key={key}
                                        colSpan={colSpan}
                                        className={colClasses.join(" ")}
                                        style={{
                                            ...gridStyles,
                                            backgroundColor: settings.background,
                                            textAlign      : (
                                                column.alignment ?
                                                    TEXT_ALIGN[column.alignment] :
                                                    HEADER_DEFAULT_ALIGNMENTS[key]
                                            ),
                                        }}
                                    >
                                        {column.name}
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody className="sale-items">
                        {data.products.map((row, index: number) => {
                            const rowStyle = {
                                ...gridStyles,
                            };

                            if(row.promotion) {
                                rowStyle.color = settings.promotionColor;
                            }

                            let isFirstColumn = true;

                            return (
                                <Fragment key={index}>
                                    <tr className="sale-line">
                                        {settings.order.map(key => {
                                            const column = settings.columns[key];

                                            if(!column.enabled) {
                                                return;
                                            }

                                            const colSpan = isFirstColumn ? 2 : 1;

                                            isFirstColumn = false;

                                            return (
                                                <td
                                                    key={index + "|" + key}
                                                    className={`cd-col cd-${key}`}
                                                    colSpan={colSpan}
                                                    style={{
                                                        ...rowStyle,
                                                        textAlign: (
                                                            column.alignment ?
                                                                TEXT_ALIGN[column.alignment] :
                                                                COLUMN_DEFAULT_ALIGNMENTS[key]
                                                        ),
                                                    }}
                                                >
                                                    {row[key]}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                    {
                                        (canDisplayComponents) &&
                                        row.components.map((component, index) => {
                                            let icon = "-";

                                            if(component.type === "added") {
                                                icon = "+";
                                            }

                                            const columns: Array<ReactNode> = [
                                                <td
                                                    key="cd-col cd-icon"
                                                    className="col icon"
                                                    style={{
                                                        ...gridStyles,
                                                        borderRight: "none",
                                                        width      : "1em",
                                                    }}
                                                >
                                                    <span
                                                        style={{
                                                            display    : "inline-block",
                                                            fontFamily : "monospace",
                                                            marginRight: "4px",
                                                        }}
                                                    >
                                                        {icon}
                                                    </span>
                                                </td>,
                                            ];

                                            const componentData: Record<keyof SaleComponentColumns, ReactNode> = {
                                                item    : component.name,
                                                quantity: "",
                                                price   : (
                                                    <NoTranslate>
                                                        {currency(component.price / row.quantity)}
                                                    </NoTranslate>
                                                ),
                                                totalPrice: <NoTranslate>{currency(component.price)}</NoTranslate>,
                                            };

                                            let isFirstColumn = true;

                                            for(const key of settings.order) {
                                                const column = settings.columns[key];

                                                if(!column.enabled) {
                                                    continue;
                                                }

                                                const style = {
                                                    ...rowStyle,
                                                    textAlign: (
                                                        column.alignment ?
                                                            TEXT_ALIGN[column.alignment] :
                                                            COLUMN_DEFAULT_ALIGNMENTS[key]
                                                    ),
                                                };

                                                if(isFirstColumn) {
                                                    style.borderLeftStyle = "none";
                                                }

                                                isFirstColumn = false;

                                                columns.push(
                                                    <td
                                                        key={index + "|" + key}
                                                        className={`cd-col cd-${key}`}
                                                        style={style}
                                                    >
                                                        {componentData[key]}
                                                    </td>
                                                );
                                            }

                                            return (
                                                <tr
                                                    key={index}
                                                    className="sale-line"
                                                >
                                                    {columns}
                                                </tr>
                                            );
                                        })
                                    }
                                </Fragment>

                            );
                        })}
                    </tbody>
                </table>
            </div>
            <div className="sale-footer" style={footerStyle}>
                {footer}
            </div>
        </div>
    );
};
